import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container, PrettyJSON } from '@ui';
import { GhostButton } from '@ui/buttons';
import ChevronDown from '@assets/icons/svgs/ChevronDown';
import { formatDateTime, formatRelative, humanizeToken, EM_DASH } from './format';
import { HeaderRow, Row, HeaderCell, LinkButton } from './styles';

// One audit event, as a grid row that expands to its before / after. Shared by the audit page and
// the history lists on the value and unmapped details, so an event reads the same everywhere.

// At | Actor | Event | Subject | Run | Reason | ▾
// Each track's minimum, and its share of the slack; the template string and the table's min-width
// are both derived from this list so the two cannot disagree.
const AUDIT_COLUMNS = [
  { min: 130, grow: '1.1fr' },
  { min: 150, grow: '1.4fr' },
  { min: 140, grow: '1.3fr' },
  { min: 130, grow: '1.2fr' },
  { min: 60, grow: '0.5fr' },
  { min: 140, grow: '1.6fr' },
  { min: 28 },
];

export const AUDIT_GRID_COLUMNS = AUDIT_COLUMNS.map(({ min, grow }) =>
  grow ? `minmax(${min}px, ${grow})` : `${min}px`
).join(' ');

// The narrowest Table that holds every track at its minimum: the mins, the 16px gap between each
// pair (styles' Row / HeaderRow), and the Row's 8px padding at either side. Both tables that draw
// these rows size to it, so neither overflows its frame.
const GRID_GAP = 16;
const ROW_PADDING_X = 8;
export const AUDIT_TABLE_MIN_WIDTH =
  AUDIT_COLUMNS.reduce((sum, { min }) => sum + min, 0) + GRID_GAP * (AUDIT_COLUMNS.length - 1) + ROW_PADDING_X * 2;

const Chevron = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform 120ms ease;

  svg {
    width: 14px;
    height: 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Toggle = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
`;

const DiffGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const DiffPane = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  font-size: 12px;
  min-width: 0;
`;

// A row and the detail it opens are one block, closed by a single rule (FeedTableRow's RowGroup):
// the before / after belongs to the row above it, so the separator goes under both rather than
// between them. The Row's own border is switched off inside the group.
const RowGroup = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  ${Row} {
    border-bottom: 0;
  }
`;

const Detail = styled.div`
  padding: 8px 8px 16px 8px;
`;

export const formatActor = (actor) => {
  if (!actor) return EM_DASH;
  if (actor.email) return actor.email;
  if (actor.type === 'system') return 'System';
  return actor.id ? `${humanizeToken(actor.type || 'user')} ${actor.id}` : humanizeToken(actor.type || 'user');
};

export const formatSubject = (subject) => {
  if (!subject) return EM_DASH;
  return `${humanizeToken(subject.type || '')} ${subject.id ?? ''}`.trim();
};

const hasContent = (value) =>
  value !== null && value !== undefined && !(typeof value === 'object' && Object.keys(value).length === 0);

export const AuditEventHeader = () => (
  <HeaderRow $columns={AUDIT_GRID_COLUMNS}>
    <HeaderCell>At</HeaderCell>
    <HeaderCell>Actor</HeaderCell>
    <HeaderCell>Event</HeaderCell>
    <HeaderCell>Subject</HeaderCell>
    <HeaderCell>Run</HeaderCell>
    <HeaderCell>Reason</HeaderCell>
    <span />
  </HeaderRow>
);

// `onOpenSubject(subject)` / `onOpenRun(runId)` are optional: without them the cells are text.
export const AuditEventRow = ({ event, onOpenSubject, onOpenRun }) => {
  const [open, setOpen] = React.useState(false);
  const detailId = React.useId();
  const expandable = hasContent(event.before) || hasContent(event.after);

  return (
    <RowGroup>
      <Row $columns={AUDIT_GRID_COLUMNS} $interactive={false}>
        <div>
          <Body7 title={event.at}>{formatDateTime(event.at)}</Body7>
          <Container top={2}>
            <Body7 isLowContrast>{formatRelative(event.at)}</Body7>
          </Container>
        </div>
        <div>
          <Body7>{formatActor(event.actor)}</Body7>
          {event.actor?.type && (
            <Container top={2}>
              <Body7 isLowContrast>{humanizeToken(event.actor.type)}</Body7>
            </Container>
          )}
        </div>
        <Body7 bold>{humanizeToken(event.event_type || '')}</Body7>
        <div>
          {onOpenSubject && event.subject?.id ? (
            <LinkButton onClick={() => onOpenSubject(event.subject)}>{formatSubject(event.subject)}</LinkButton>
          ) : (
            <Body7>{formatSubject(event.subject)}</Body7>
          )}
        </div>
        <div>
          {event.run_id ? (
            onOpenRun ? (
              <LinkButton onClick={() => onOpenRun(event.run_id)}>#{event.run_id}</LinkButton>
            ) : (
              <Body7>#{event.run_id}</Body7>
            )
          ) : (
            <Body7 isLowContrast>{EM_DASH}</Body7>
          )}
        </div>
        <Body7 isLowContrast title={event.reason || undefined}>
          {event.reason || EM_DASH}
        </Body7>
        <div>
          {expandable && (
            <Toggle
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={detailId}
              aria-label={open ? 'Hide before and after' : 'Show before and after'}
            >
              <Chevron $open={open} aria-hidden="true">
                <ChevronDown color="currentColor" />
              </Chevron>
            </Toggle>
          )}
        </div>
      </Row>
      {open && expandable && (
        <Detail id={detailId}>
          <DiffGrid>
            <DiffPane>
              <Body5 bold>Before</Body5>
              {hasContent(event.before) ? <PrettyJSON value={event.before} /> : <Body7 isLowContrast>Nothing</Body7>}
            </DiffPane>
            <DiffPane>
              <Body5 bold>After</Body5>
              {hasContent(event.after) ? <PrettyJSON value={event.after} /> : <Body7 isLowContrast>Nothing</Body7>}
            </DiffPane>
          </DiffGrid>
        </Detail>
      )}
    </RowGroup>
  );
};
